import React from 'react';
import { CheckCircle2, CircleDot } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const MethodologySection = () => {
  const phases = [
    {
      phase: '01',
      title: 'Initiation',
      description: 'Define project scope, objectives, and stakeholders. Conduct feasibility studies and establish project charter.',
      points: [
        'Stakeholder identification',
        'Project charter creation',
        'Feasibility analysis',
        'Initial risk assessment',
      ],
    },
    {
      phase: '02',
      title: 'Planning',
      description: 'Develop comprehensive project plans including timelines, resources, budget, and risk management strategies.',
      points: [
        'Scope definition',
        'Resource allocation',
        'Timeline development',
        'Budget planning',
      ],
    },
    {
      phase: '03',
      title: 'Execution',
      description: 'Implement project plans, coordinate team activities, manage resources, and ensure quality deliverables.',
      points: [
        'Team coordination',
        'Quality assurance',
        'Progress monitoring',
        'Stakeholder communication',
      ],
    },
    {
      phase: '04',
      title: 'Monitoring & Control',
      description: 'Track project performance, manage changes, and ensure alignment with objectives through continuous oversight.',
      points: [
        'Performance tracking',
        'Change management',
        'Risk mitigation',
        'Quality control',
      ],
    },
    {
      phase: '05',
      title: 'Closure',
      description: 'Finalize deliverables, conduct project review, document lessons learned, and ensure smooth handover.',
      points: [
        'Final deliverables',
        'Project documentation',
        'Lessons learned',
        'Stakeholder handover',
      ],
    },
  ];

  const methodologies = [
    'Agile/Scrum',
    'Waterfall',
    'Hybrid Approach',
    'Lean Management',
    'Six Sigma',
    'PRINCE2',
  ];

  return (
    <section id="methodology" className="py-24 bg-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-[#F5F1E8] to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Our <span className="text-[#8B2635]">Methodology</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-[#8B2635] to-[#A63446] mx-auto mb-8" />
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            A structured, proven approach to project management that delivers consistent, exceptional results.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Connecting Line */}
          <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-[#8B2635] via-[#A63446] to-[#8B2635]" />

          {phases.map((item, index) => (
            <div
              key={index}
              className={`relative mb-16 lg:mb-24 animate-fade-in-up`}
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              <div className={`lg:flex items-center ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                {/* Content Card */}
                <div className="lg:w-5/12">
                  <Card className="group hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border-0 bg-gradient-to-br from-white to-gray-50">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="text-6xl font-black text-[#8B2635]/10 leading-none">{item.phase}</div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-[#8B2635] transition-colors">
                            {item.title}
                          </h3>
                          <p className="text-gray-600 leading-relaxed mb-6">{item.description}</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        {item.points.map((point, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                            <CheckCircle2 className="h-5 w-5 text-[#8B2635] flex-shrink-0" />
                            <span className="text-gray-700">{point}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Center Dot */}
                <div className="hidden lg:flex lg:w-2/12 justify-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#8B2635] to-[#A63446] rounded-full flex items-center justify-center shadow-xl transform group-hover:scale-125 transition-transform duration-300">
                    <CircleDot className="h-8 w-8 text-white" />
                  </div>
                </div>

                {/* Spacer */}
                <div className="hidden lg:block lg:w-5/12" />
              </div>
            </div>
          ))}
        </div>

        {/* Methodologies */}
        <div className="mt-20 text-center">
          <h3 className="text-3xl font-bold text-gray-900 mb-8">Supported Methodologies</h3>
          <div className="flex flex-wrap justify-center gap-4">
            {methodologies.map((method, index) => (
              <div
                key={index}
                className="px-6 py-3 bg-gradient-to-r from-[#8B2635] to-[#A63446] text-white rounded-full font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {method}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MethodologySection;